import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Radio,
  Switch,
  Popconfirm,
  Space,
  Typography,
  Tag,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import TW from "antd/lib/date-picker/locale/zh_TW";
import moment from "moment";
import "moment/locale/zh-tw";
import { PricingAPI } from "../../../controller/PricingAPI";

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"];

export default function HolidayPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(moment());
  const [showRemoved, setShowRemoved] = useState(false);
  const [category, setCategory] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [form] = Form.useForm();

  // 一律連同軟刪除一起取回，已移除的列在前端切換顯示 ——
  // 切換一次就打一次 API 沒有意義，整年也才百餘列
  const load = useCallback(() => {
    setLoading(true);
    const from = year.clone().startOf("year").format("YYYY-MM-DD");
    const to = year.clone().endOf("year").format("YYYY-MM-DD");
    Promise.all([
      PricingAPI.holiday.index(from, to, true),
      PricingAPI.holiday.workdays(from, to),
    ])
      .then(([holidays, workdays]) =>
        setList(
          [...(holidays.data || []), ...(workdays.data || [])].sort((a, b) =>
            a.date < b.date ? -1 : 1
          )
        )
      )
      .catch(() => message.error("載入假日設定失敗"))
      .finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = () => {
    form.validateFields().then((v) => {
      PricingAPI.holiday
        .create({ date: v.date.format("YYYY-MM-DD"), name: v.name || null })
        .then(() => {
          message.success("已新增");
          setModalOpen(false);
          load();
        })
        .catch((error) => {
          const response = error && error.response;
          // 422 的原因不只一種 (已經是假日、是官方補班日)，後端講得比前端準
          const serverMessage = response && response.data && response.data.message;
          message.error(response && response.status === 422 && serverMessage ? serverMessage : "新增失敗");
        });
    });
  };

  const remove = (row) => {
    PricingAPI.holiday
      .remove(row.date)
      .then(() => {
        message.success("已移除");
        load();
      })
      .catch(() => message.error("移除失敗"));
  };

  const restore = (row) => {
    PricingAPI.holiday
      .restore(row.date)
      .then(() => {
        message.success("已還原");
        load();
      })
      .catch(() => message.error("還原失敗"));
  };

  const refetch = () => {
    setFetching(true);
    PricingAPI.holiday
      .fetch()
      .then(() => {
        message.success("已重新抓取官方日曆");
        load();
      })
      .catch(() => message.error("抓取失敗，官方網站可能暫時無法連線"))
      .finally(() => setFetching(false));
  };

  const rows = list
    .filter((row) => showRemoved || !row.deleted_at)
    .filter((row) => category === "all" || row.category === category);

  const columns = [
    { title: "日期", dataIndex: "date", key: "date" },
    {
      title: "星期",
      key: "weekday",
      align: "center",
      render: (t, row) => WEEKDAYS[moment(row.date).day()],
    },
    {
      title: "類型",
      dataIndex: "category",
      key: "category",
      align: "center",
      render: (v) => {
        if (v === "weekend") return <Tag color="red">例假日</Tag>;
        if (v === "workday") return <Tag color="blue">補班日</Tag>;
        return <Tag color="orange">特別假日</Tag>;
      },
    },
    { title: "名稱", dataIndex: "name", key: "name" },
    {
      title: "來源",
      dataIndex: "source",
      key: "source",
      align: "center",
      render: (v) => (v === "manual" ? "人工" : "官方"),
    },
    {
      // 生效中的假日留空 —— 整頁絕大多數都是生效中，逐列標一個「假日」等於沒有資訊，
      // 只會蓋掉真正需要被看見的「已移除」
      title: "狀態",
      key: "status",
      align: "center",
      render: (t, row) => (row.deleted_at ? <Tag color="default">已移除</Tag> : null),
    },
    {
      title: "操作",
      key: "action",
      render: (t, row) => {
        // 補班日是官方公告的事實，這裡維持唯讀 —— 提供一顆會 404 的按鈕比沒有按鈕更糟
        if (row.category === "workday") return null;
        return row.deleted_at ? (
          <Button size="small" onClick={() => restore(row)}>還原</Button>
        ) : (
          <Popconfirm
            title={`移除 ${row.date}?`}
            onConfirm={() => remove(row)}
            okText="移除"
            cancelText="取消"
          >
            <Button size="small" danger>移除</Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div>
      <Typography.Title level={3} style={{ marginBottom: 8 }}>假日設定</Typography.Title>

      <Typography.Paragraph type="secondary">
        資料來自政府資料開放平臺「中華民國政府行政機關辦公日曆表」，每月 1 日自動更新。
        判定依官方的「是否放假」欄位而非星期幾 —— 六日照樣可能是春節或和平紀念日，
        而補班的週六看起來像週末、實際上要上班。補班日為官方公告的事實，不可增刪。
        人工移除的假日會保留移除記錄，自動抓取不會再把它加回來。
      </Typography.Paragraph>

      {/* 篩選與操作獨立一列，緊鄰它們作用的表格;標題自己一列不與控制項混排 */}
      <Space style={{ marginBottom: 12 }} align="center" wrap>
        <DatePicker
          allowClear={false}
          picker="year"
          locale={TW}
          value={year}
          onChange={(v) => v && setYear(v)}
        />
        <Radio.Group value={category} onChange={(e) => setCategory(e.target.value)}>
          <Radio.Button value="all">全部</Radio.Button>
          <Radio.Button value="weekend">例假日</Radio.Button>
          <Radio.Button value="special">特別假日</Radio.Button>
          <Radio.Button value="workday">補班日</Radio.Button>
        </Radio.Group>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModalOpen(true); }}>
          新增假日
        </Button>
        <Button icon={<ReloadOutlined />} loading={fetching} onClick={refetch}>重新抓取</Button>
        <Space size="small">
          <Switch checked={showRemoved} onChange={setShowRemoved} />
          <span>顯示已移除</span>
        </Space>
      </Space>

      <Table
        rowKey="date"
        loading={loading}
        columns={columns}
        dataSource={rows}
        size="small"
        pagination={false}
        scroll={{ x: "max-content", y: 600 }}
      />

      <Modal
        title="新增假日"
        visible={modalOpen}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        okText="新增"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="date" label="日期" rules={[{ required: true, message: "請選擇日期" }]}>
            <DatePicker locale={TW} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="name" label="名稱" extra="例如 暑假旺季、館內活動；可留空，類型仍是特別假日">
            <Input placeholder="暑假旺季" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
